/**
 * Packaging catalogue (custom): instant filter chips for sections/packaging-catalogue-custom.
 *
 * Every card is already on the page, so filtering only hides cards and the groups
 * left empty. Chips in different rows combine (size AND shape); within a row one
 * value is active at a time. A card's data attribute may hold several
 * space-separated values (an oil in two families), and matches any of them. A row
 * with a single chip and no "All" works as a toggle (in stock only). Each chip's
 * count is what the page would show if it were picked, given the other rows, and a
 * chip that would show nothing is marked aria-disabled rather than removed, so the
 * rows do not jump.
 *
 * An optional name search ([data-search-input]) narrows the cards whose data-search
 * holds every typed word, ignoring case and accents (and Arabic short vowels). An
 * optional sort select ([data-sort-select]) reloads the page with Shopify's
 * ?sort_by=, keeping the filters.
 *
 * A collection larger than Shopify's 250-product page renders a [data-more-url]
 * marker; the following pages' cards are fetched and appended before the chips
 * start, so the filters and counts cover the whole collection.
 *
 * The state lives in the URL hash (#size=50-55&shape=dome&q=rose) so a filtered view
 * can be shared, without creating a second crawlable URL for the collection.
 */
const ccFold = (text) => (text || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f\u064b-\u065f\u0670]/g, '')
  .trim();

if (!customElements.get('packaging-catalogue')) {
  class PackagingCatalogue extends HTMLElement {
    connectedCallback() {
      const more = this.querySelector('[data-more-url]');
      if (more) {
        this.loadMore(more).finally(() => this.init());
      } else {
        this.init();
      }
    }

    /**
     * Appends the cards of the section's following pages, for a collection larger
     * than Shopify's 250-product page ([data-more-url] is the next page's
     * Section Rendering URL). A failed fetch leaves the cards already shown.
     * @param {Element} marker - Element carrying data-more-url.
     */
    async loadMore(marker) {
      const grid = this.querySelector('[data-grid]');
      let url = marker.dataset.moreUrl;
      marker.remove();
      while (url && grid) {
        try {
          const response = await fetch(url);
          if (!response.ok) break;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          grid.append(...doc.querySelectorAll('[data-grid] > [data-card]'));
          url = doc.querySelector('[data-more-url]')?.dataset.moreUrl;
        } catch (error) {
          break;
        }
      }
    }

    init() {
      this.chips = [...this.querySelectorAll('.cc-chip[data-dim]')];
      this.search = this.querySelector('[data-search-input]');
      this.searchClear = this.querySelector('[data-search-clear]');
      this.sort = this.querySelector('[data-sort-select]');
      if (this.sort) this.sort.addEventListener('change', this.handleSort.bind(this));
      if (!this.chips.length && !this.search) return;

      this.cards = [...this.querySelectorAll('[data-card]')];
      this.words = new Map(this.cards.map((card) => [card, ccFold(card.dataset.search)]));
      this.query = [];
      this.groups = [...this.querySelectorAll('[data-group]')];
      this.status = this.querySelector('[data-status]');
      this.empty = this.querySelector('[data-empty]');
      this.dims = [...new Set(this.chips.map((chip) => chip.dataset.dim))];
      this.state = {};

      this.readHash();
      this.addEventListener('click', this.handleClick.bind(this));
      if (this.search) {
        let timer;
        this.search.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.setQuery(this.search.value, true), 120);
        });
        this.search.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter') evt.preventDefault();
        });
      }
      this.onHashChange = () => {
        this.readHash();
        this.apply(false);
      };
      window.addEventListener('hashchange', this.onHashChange);
      this.apply(false);
    }

    disconnectedCallback() {
      if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
    }

    /**
     * Sets the name search and refreshes the page.
     * @param {string} value - Raw text.
     * @param {boolean} announce - User-initiated.
     */
    setQuery(value, announce) {
      this.queryText = value.trim();
      this.query = ccFold(value).split(/\s+/).filter(Boolean);
      if (this.searchClear) this.searchClear.hidden = !this.queryText;
      this.apply(announce);
    }

    /** Reloads with the chosen Shopify sort, keeping the other query parameters and the hash. */
    handleSort() {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.sort.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }

    /** Reads filters from the hash, ignoring anything that is not a chip on this page. */
    readHash() {
      this.state = {};
      const params = new URLSearchParams(window.location.hash.slice(1));
      if (this.search) {
        const q = params.get('q') || '';
        this.search.value = q;
        this.queryText = q.trim();
        this.query = ccFold(q).split(/\s+/).filter(Boolean);
        if (this.searchClear) this.searchClear.hidden = !this.queryText;
      }
      this.dims.forEach((dim) => {
        const value = params.get(dim);
        if (value && this.chips.some((chip) => chip.dataset.dim === dim && chip.dataset.value === value)) {
          this.state[dim] = value;
        }
      });
    }

    writeHash() {
      const params = new URLSearchParams(window.location.hash.slice(1));
      if (this.search) {
        if (this.queryText) {
          params.set('q', this.queryText);
        } else {
          params.delete('q');
        }
      }
      this.dims.forEach((dim) => {
        if (this.state[dim]) {
          params.set(dim, this.state[dim]);
        } else {
          params.delete(dim);
        }
      });
      const hash = params.toString();
      const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`;
      window.history.replaceState(window.history.state, '', url);
    }

    handleClick(evt) {
      if (evt.target.closest('[data-search-clear]')) {
        this.search.value = '';
        this.setQuery('', true);
        this.search.focus();
        return;
      }

      const clear = evt.target.closest('[data-clear]');
      if (clear) {
        this.state = {};
        if (this.search) {
          this.search.value = '';
          this.queryText = '';
          this.query = [];
          if (this.searchClear) this.searchClear.hidden = true;
        }
        this.apply(true);
        const first = this.chips.find((chip) => chip.dataset.value === '');
        if (first) first.focus();
        return;
      }

      const chip = evt.target.closest('.cc-chip[data-dim]');
      if (!chip || chip.getAttribute('aria-disabled') === 'true') return;

      const { dim, value } = chip.dataset;
      if (value && this.state[dim] !== value) {
        this.state[dim] = value;
      } else {
        delete this.state[dim];
      }
      this.apply(true);
    }

    /**
     * Whether a card passes every active filter, optionally ignoring one row.
     * @param {Element} card - Card element.
     * @param {string} [skipDim] - Row to leave out.
     * @returns {boolean}
     */
    cardMatches(card, skipDim) {
      if (this.query.length) {
        const words = this.words.get(card);
        if (!this.query.every((word) => words.includes(word))) return false;
      }
      return this.dims.every((dim) => dim === skipDim || !this.state[dim] || PackagingCatalogue.has(card, dim, this.state[dim]));
    }

    /**
     * Whether a card carries a value for a row.
     * @param {Element} card - Card element.
     * @param {string} dim - Row.
     * @param {string} value - Value.
     * @returns {boolean}
     */
    static has(card, dim, value) {
      return (card.dataset[dim] || '').split(' ').includes(value);
    }

    /**
     * Shows the cards and groups that match, and refreshes chips, counts and the status line.
     * @param {boolean} announce - Update the live region and the URL (user-initiated changes).
     */
    apply(announce) {
      let shown = 0;
      this.cards.forEach((card) => {
        const visible = this.cardMatches(card);
        card.hidden = !visible;
        if (visible) shown += 1;
      });

      this.groups.forEach((group) => {
        const count = group.querySelectorAll('[data-card]:not([hidden])').length;
        group.hidden = count === 0;
        const counter = group.querySelector('[data-group-count]');
        if (counter) counter.textContent = count;
      });

      this.chips.forEach((chip) => {
        const { dim, value } = chip.dataset;
        const pressed = value ? this.state[dim] === value : !this.state[dim];
        const count = this.cards.filter((card) => this.cardMatches(card, dim) && (!value || PackagingCatalogue.has(card, dim, value))).length;
        chip.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        const counter = chip.querySelector('[data-count]');
        if (counter) counter.textContent = count;
        if (count === 0 && !pressed) {
          chip.setAttribute('aria-disabled', 'true');
        } else {
          chip.removeAttribute('aria-disabled');
        }
      });

      const total = this.cards.length;
      this.toggleAttribute('data-filtered', shown !== total);
      if (this.empty) this.empty.hidden = shown !== 0;

      if (announce) {
        if (this.status) {
          this.status.textContent = this.dataset.statusTemplate
            .replace('[count]', shown)
            .replace('[total]', total);
        }
        this.writeHash();
      }
    }
  }

  customElements.define('packaging-catalogue', PackagingCatalogue);
}
